import { useEffect, useState } from "react";
import { BrowserProvider } from "ethers";
import { toast } from "sonner";
import { X, Mail, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Logo } from "./Logo";

export default function AuthModal({ open, onClose }) {
  const { setAuth } = useAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const close = () => {
    if (!busy) onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { email, password, username };
      const { data } = await api.post(path, body);
      setAuth(data.access_token, data.user);
      toast.success(mode === "login" ? "Welcome back" : "Account created — 1,000 FMI bonus credited");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const connectWallet = async () => {
    if (!window.ethereum) {
      toast.error("MetaMask not detected. Please install the extension.");
      return;
    }
    setBusy(true);
    try {
      const provider = new BrowserProvider(window.ethereum);
      const accounts = await provider.send("eth_requestAccounts", []);
      const address = accounts[0];
      const signer = await provider.getSigner();
      const chal = await api.post("/auth/wallet/challenge", { address });
      const message = chal.data.message;
      const signature = await signer.signMessage(message);
      const { data } = await api.post("/auth/wallet/verify", { address, message, signature });
      setAuth(data.access_token, data.user);
      toast.success(`Signed in as ${address.slice(0, 6)}…${address.slice(-4)}`);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.shortMessage || err?.message || "Wallet login failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(8px)" }}
      onClick={close}
      data-testid="auth-modal"
    >
      <div
        className="relative panel w-full max-w-[440px] p-7 fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute right-4 top-4 w-8 h-8 rounded-full flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/5"
          data-testid="auth-close"
        >
          <X size={16} />
        </button>

        <div className="mb-6">
          <div className="mb-4" data-testid="auth-logo"><Logo /></div>
          <div className="font-display text-2xl font-black tracking-tighter">
            Enter the <span className="gold-text">Vault</span>
          </div>
          <div className="text-sm text-zinc-500 mt-1">Choose your path. Non-custodial or classic.</div>
        </div>

        <button
          onClick={connectWallet}
          disabled={busy}
          className="btn-gold w-full rounded-xl py-3.5 flex items-center justify-center gap-2 mb-3"
          data-testid="metamask-connect-btn"
        >
          <Wallet size={16} /> Connect MetaMask
        </button>
        <div className="text-[10px] text-zinc-600 text-center mb-5 tracking-wider uppercase">
          Signature only · No transaction · No gas
        </div>

        <div className="flex items-center gap-3 my-4">
          <div className="hairline flex-1" />
          <span className="text-[10px] text-zinc-600 tracking-wider uppercase">Or email</span>
          <div className="hairline flex-1" />
        </div>

        <div className="flex items-center gap-1 mb-4 p-1 panel">
          {["login", "register"].map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              data-testid={`auth-tab-${m}`}
              className={`flex-1 text-xs uppercase tracking-wider py-2 rounded-lg ${
                mode === m ? "bg-white/[0.08] text-white" : "text-zinc-500 hover:text-white"
              }`}
              style={{ transition: "background-color 180ms ease, color 180ms ease" }}
            >
              {m === "login" ? "Sign in" : "Register"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="flex flex-col gap-3">
          {mode === "register" && (
            <input
              type="text"
              placeholder="Username (optional)"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="panel px-4 py-3 outline-none focus:border-amber-500/40 bg-transparent"
              style={{ background: "#0F0F11" }}
              data-testid="auth-username"
            />
          )}
          <div className="relative">
            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              type="email"
              required
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="panel w-full pl-10 pr-4 py-3 outline-none focus:border-amber-500/40 bg-transparent"
              style={{ background: "#0F0F11" }}
              data-testid="auth-email"
            />
          </div>
          <input
            type="password"
            required
            minLength={6}
            placeholder="Password (min 6 chars)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="panel px-4 py-3 outline-none focus:border-amber-500/40 bg-transparent"
            style={{ background: "#0F0F11" }}
            data-testid="auth-password"
          />
          <button
            type="submit"
            disabled={busy}
            className="btn-cyan rounded-xl py-3 mt-1"
            data-testid="auth-submit"
          >
            {busy ? "…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div className="mt-5 text-center text-[11px] text-zinc-600">
          By continuing you agree to play responsibly.
        </div>
      </div>
    </div>
  );
}
